/**
 * The reference's mosaic strip — `mosaicHTML` (`m01-utils.js:263`) plus the router's own fill
 * (`m10-router.js:127`).
 *
 * Two surfaces use it: the login screen's `48x3` strip and the public credential visual's `6x…`
 * corner. The router walks every `[data-mosaic]` once at boot, reads `cols x rows` off the
 * attribute, sets `--cols` INLINE and fills the element — so a faithful port carries the attribute
 * and the inline custom property, not just the tiles.
 *
 * The arithmetic is a fixed pseudo-pattern, not a random one. Reproducing it exactly is the only
 * way the strip matches; a different seed paints a different picture across the top of the screen.
 * Verified against the running reference: 144 cells, 0 differing.
 */
import type { CSSProperties, ReactElement } from 'react';

export const MOSAIC_COLORS = ['#CF051E', '#F8BA3F', '#000000'];

export function mosaicTiles(cols: number, rows: number, colors = MOSAIC_COLORS, seed = 5): string[] {
  const tiles: string[] = [];
  let i = seed;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const on = (i * 37) % 7 < 4;
      const color = colors[(i * 53) % colors.length];
      tiles.push(on && color !== undefined ? color : 'transparent');
      i++;
    }
  }
  return tiles;
}

export function Mosaic({
  cols,
  rows,
  className,
  colors,
}: {
  cols: number;
  rows: number;
  className: string;
  colors?: string[];
}): ReactElement {
  return (
    <div className={className} data-mosaic={`${cols}x${rows}`} style={{ '--cols': cols } as CSSProperties}>
      {mosaicTiles(cols, rows, colors).map((background, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a tile's position IS its identity here
        <i key={index} style={{ background }} />
      ))}
    </div>
  );
}
